import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { AccessControlService } from '../services/access-control.service';
import { AppUserService } from '../services/app-user.service';
import { AttachmentService } from '../services/attachment.service';
import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private tokens: TokenStorageService,
    private auth: AuthService,
    private router: Router,
    private access: AccessControlService,
    private appUsers: AppUserService,
    private attachments: AttachmentService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isAuthEndpoint(req)) {
      return next.handle(req);
    }

    const tokenizedRequest = this.addToken(req, this.tokens.accessToken);

    return next.handle(tokenizedRequest).pipe(
      catchError(error => this.handleAuthError(error, req, next))
    );
  }

  private handleAuthError(
    error: unknown,
    originalReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!(error instanceof HttpErrorResponse) || !this.isAuthStatus(error.status)) {
      return throwError(() => error);
    }

    // Preview URLs are non-critical. If they fail, let the caller fall back to a default image.
    if (this.isPreviewEndpoint(originalReq)) {
      return throwError(() => error);
    }

    if (error.status !== 401) {
      return throwError(() => error);
    }

    if (!this.tokens.refreshToken || this.tokens.isRefreshExpired) {
      this.forceLogoutUi();
      return throwError(() => error);
    }

    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(originalReq, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.auth.refresh().pipe(
      switchMap(() => {
        const newToken = this.tokens.accessToken;
        if (!newToken) {
          this.forceLogoutUi();
          return throwError(() => new Error('No access token present after refresh'));
        }
        this.refreshSubject.next(newToken);
        return next.handle(this.addToken(originalReq, newToken));
      }),
      catchError(refreshError => {
        this.forceLogoutUi();
        return throwError(() => refreshError);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (!token) {
      return req;
    }

    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private isAuthEndpoint(req: HttpRequest<any>): boolean {
    return /\/auth\/(token|refresh|register|register\/otp|password\/reset|password\/reset\/otp)$/.test(req.url);
  }

  private isAuthStatus(status: number): boolean {
    return status === 401 || status === 403;
  }

  private isPreviewEndpoint(req: HttpRequest<any>): boolean {
    return /\/files\/preview-url(?:-attachment)?$/.test(req.url);
  }

  private forceLogoutUi(): void {
    this.tokens.clear();
    this.access.clear();
    this.appUsers.clearCurrentProfileCache();
    this.attachments.clearPreviewUrlCache();
    void this.router.navigate(['/signIn']);
  }
}

export const AUTH_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true
};
